import React, { useCallback, useEffect, useState } from 'react'
import Page, { Container, PageHeader, Row } from '@zera-admin/page'
import Spinner from '@zera-admin/spinner'
import Button from '@zera-admin/button'
import Icon from '@zera-admin/icon'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import MainLayout from 'layouts/main'
import mobileReportService from 'services/http/report/agenda-report'
import { Data, HashtagDetail, MobileReportProps } from './types'
import { LoadingView } from 'pages/person-report/PersonReport.styled'
import * as Styled from './TouristDisturbance.styled'
import { PdfContainer } from './components/twitter-card'
import Overlay from './components/overlay/Overlay'
import TwitterTweetCard from './components/tweet'
import Header from './components/header'
import Users from './components/users'
import TouristDisturbanceGraph from './components/graph'
import Hashtags from './components/hashtags'
import SocialCard from './socialcard/SocialCard'
import NewsCard from './components/news'
import RedditCard from './components/reddit'
import WordCloud from './components/wordcloud'
import TwitterLogo from '../../../assets/images/twitter-new-logo-light.png'

const MobileReport: React.FunctionComponent<MobileReportProps> = ({
  match,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false)
  const [pdfInputValue, setPdfInputValue] = useState('')
  const [touristDisturbanceReport, setTouristDisturbanceReport] = useState<Data>()
  const [showFullTextList, setShowFullTextList] = useState<boolean[]>([])
  const [selectedType, setSelectedType] = useState<'TR' | 'OTH' | null>(null)
  const [showTypeSelector, setShowTypeSelector] = useState<boolean>(true)
  const [retryCount, setRetryCount] = useState<number>(0)
  const [maxRetries] = useState<number>(3)
  const [retryDelay] = useState<number>(1000)

  // URL'den Type parametresini oku
  const getTypeFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const type = urlParams.get('Type')
    return type === 'TR' || type === 'OTH' ? type : null
  }

  // URL'yi güncelle
  const updateURL = (type: 'TR' | 'OTH') => {
    const newURL = `${window.location.pathname}?Type=${type}`
    window.history.pushState({}, '', newURL)
  }

  // URL'yi temizle (tip seçimi sayfasına dön)
  const clearURL = () => {
    const newURL = window.location.pathname
    window.history.pushState({}, '', newURL)
  }

  const updateShowFullTextList = (index: number, showFullText: boolean) => {
    const updatedList = [...showFullTextList]
    updatedList[index] = showFullText
    setShowFullTextList(updatedList)
  }

  const colors = [
    '#E69E15',
    '#D13FD6',
    '#20D496',
    '#269FFB',
    '#E69E15',
    '#D13FD6',
    '#20D496',
    '#269FFB',
  ]

  const Dteparse = (date: string) => {
    const dates = new Date(date)
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }
    return dates.toLocaleString('tr-TR', options)
  }

  let liste: any = []
  let categories: any = []
  let timesize = 0
  let timeserie: HashtagDetail = {
    hashtag: '',
    count: 0,
    timeSeries: [],
    tweets: [],
    news: [],
    reddit: [],
    influentialPeople: [],
    postCountByUserBotDetection: [],
  }

  // timeSeries'i boş olan hashtag'leri filtrele
  const filteredHashtags = touristDisturbanceReport?.hashtagDetails.filter(
    (item) => item.timeSeries && item.timeSeries.length > 0
  ) || [];

  filteredHashtags.forEach(function (time: HashtagDetail) {
    if (time.timeSeries.length > timesize) {
      timesize = time.timeSeries.length
      timeserie = time
    }
    timeserie?.timeSeries?.map((itm: any) =>
      categories?.push(`${Dteparse(itm.asDateTime)}`)
    )
  })

  filteredHashtags.map((item: HashtagDetail) =>
    liste.push({
      name: item.hashtag,
      data: item.timeSeries.map((item: any) => item.count),
    })
  )
  
  const DailyReport = useCallback((type: 'TR' | 'OTH') => {
    setIsLoading(true);
    setRetryCount(0);

    const attemptRequest = (attempt: number) => {
      mobileReportService
        .TouristDisturbanceReport(type)
        .then((res) => {
          setRetryCount(0);
          setTouristDisturbanceReport(res.data.data); // Servisten gelen camelCase veri doğrudan kullanılıyor
          setIsLoading(false);
        })
        .catch((err: Error) => {
          console.log(`API attempt ${attempt} failed:`, err);
          
          if (attempt < maxRetries) {
            setRetryCount(attempt);
            setTimeout(() => {
              attemptRequest(attempt + 1);
            }, retryDelay);
          } else {
            setIsLoading(false);
            alert('Rapor yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
          }
        });
    };

    attemptRequest(1);
  }, [maxRetries, retryDelay]);

  const handleTypeSelect = (type: 'TR' | 'OTH') => {
    setSelectedType(type)
    setShowTypeSelector(false)
    updateURL(type)
    DailyReport(type)
  }

  const handleBackToTypeSelector = () => {
    setShowTypeSelector(true)
    setSelectedType(null)
    setTouristDisturbanceReport(undefined)
    clearURL()
  }

  // Component mount olduğunda URL'den tip kontrolü yap
  useEffect(() => {
    const urlType = getTypeFromURL()
    if (urlType) {
      setSelectedType(urlType)
      setShowTypeSelector(false)
      DailyReport(urlType)
    }
  }, [DailyReport])

  const handleDownloadPdf = () => {
    setIsPdfLoading(true)
    let input = document.getElementById('printThis')!

    html2canvas(input, {
      scale: 1,
      useCORS: true,
      ignoreElements: (element: any) => {
        if (element.id === 'card-box') {
          element.style.boxShadow = 'none'
          element.style.border = '1px solid lightgray'
        } else if (element.id === 'profileShadow') {
          element.style.boxShadow = 'none'
          element.style.padding = '0px'
          element.style.border = '1px solid gray'
          element.style.height = '70px'
        }
        return false
      },
    })
      .then((canvas) => {
        var imgData = canvas.toDataURL('image/jpeg')

        var pdf = new jsPDF('p', 'in', [6, 100], true)

        // if (mobileReport?.negativePersonalitiesAndInteractions.length) {
        //   pdf = new jsPDF('p', 'in', [6, 100], true)
        // }
        var imgProps = pdf.getImageProperties(imgData)
        var pdfWidth = pdf.internal.pageSize.getWidth()
        var pdfHeight = (imgProps.height * pdfWidth) / imgProps.width
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)

        const date = new Date()
          .toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          })
          .replaceAll(':', '_')

        pdf.save(`TuristRahatsizligiRaporu-${date}.pdf`.replaceAll(' ', '_'))

        const fileName = `TuristRahatsizligiRaporu-${date}`.replaceAll(' ', '_')
        setPdfInputValue(fileName)
      })
      .catch((e) => console.log('error', e))
      .finally(() => {
        setIsPdfLoading(false)
      })
  }

  if (showTypeSelector) {
    return (
      <MainLayout isLoading={false}>
        <Container width="wide">
          <Page style={{ width: '100%' }}>
            <PageHeader>
              TURİST RAHATSIZLIĞI Raporu - Tip Seçimi
            </PageHeader>
            <Row xs={{ direction: 'column', align: 'center' }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '20px', 
                alignItems: 'center',
                padding: '50px 0'
              }}>
                <h3>Lütfen rapor tipini seçin:</h3>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <Button
                    onClick={() => handleTypeSelect('TR')}
                    style={{ minWidth: '120px' }}
                  >
                    TR Tipi
                  </Button>
                  <Button
                    onClick={() => handleTypeSelect('OTH')}
                    style={{ minWidth: '120px' }}
                  >
                    OTH Tipi
                  </Button>
                </div>
              </div>
            </Row>
          </Page>
        </Container>
      </MainLayout>
    )
  }

  return isLoading || isPdfLoading ? (
    <LoadingView>
      <Spinner size="large" />
      <h5>
        {isPdfLoading 
          ? 'PDF Hazırlanıyor...' 
          : retryCount > 0 
            ? `Rapor yükleniyor... (Deneme ${retryCount + 1}/${maxRetries})`
            : 'Rapor yükleniyor...'
        }
      </h5>
    </LoadingView>
  ) : (
    <MainLayout isLoading={isLoading}>
      <Container width="wide">
        <Page style={{ width: '100%' }}>
          <div id="stress-header">
          <PageHeader
            actions={
              <>
                <input id="pdf-date" type="hidden" value={pdfInputValue} />
                <Button
                  id="pdf-button"
                  iconBefore={<Icon name="download" size="small" />}
                  onClick={() => handleDownloadPdf()}
                >
                  PDF olarak indir
                </Button>
                <Button
                  style={{ marginLeft: '10px' }}
                  onClick={handleBackToTypeSelector}
                >
                  Tip Değiştir
                </Button>
              </>
            }
          >
            {selectedType === 'TR'
              ? 'Turistleri Rahatsız Eden Davranışlar Raporu - Türkiye'
              : selectedType === 'OTH'
              ? 'Turistleri Rahatsız Eden Davranışlar Raporu - Yabancı'
              : ''}
          </PageHeader>
           </div>
          <Row xs={{ direction: 'column', align: 'center' }}>
            <PdfContainer
              style={{ width: '100%', boxShadow: 'none' }}
              id="printThis"
              className="fa-Page"
            >
              <div
                style={{ width: '95%', borderWidth: 0.6, borderColor: 'gray' }}
              >
                <Header
                  date={touristDisturbanceReport?.date}
                  title={selectedType === 'TR'
                    ? 'Turistleri Rahatsız Eden Davranışlar Raporu - Türkiye'
                    : selectedType === 'OTH'
                    ? 'Turistleri Rahatsız Eden Davranışlar Raporu - Yabancı'
                    : ''}
                />
                <Styled.Container>
                  <div className="header-container">
                    <div className="chart-container">
                      {(liste.length && categories.length && (
                        <TouristDisturbanceGraph series={liste} categories={categories} />
                      )) || <h1>No Data</h1>}
                    </div>
                    <div className="socialcards-container">
                      {touristDisturbanceReport?.postCountByType?.map((itm: any, index: number) => (
                        <SocialCard
                          styles={{
                            borderTopLeftRadius: index === 0 ? 10 : 0,
                            borderTopRightRadius: index === 2 ? 10 : 0,
                          }}
                          tweetTitle={itm.type}
                          tweetCount={itm.count.toLocaleString()}
                          img={TwitterLogo}
                          color={'#50aaf5'}
                        />
                      ))}
                      <SocialCard
                        styles={{ borderBottomLeftRadius: 10 }}
                        tweetTitle={'Tahmini Etki'}
                        tweetCount={touristDisturbanceReport?.totalImpressionCount.toLocaleString()}
                        img={TwitterLogo}
                        color={'#50aaf5'}
                      />
                      <SocialCard
                        tweetTitle={'Tekil Kullanıcı'}
                        tweetCount={touristDisturbanceReport?.uniqueUserCount.toLocaleString()}
                        img={TwitterLogo}
                        color={'#50aaf5'}
                      />
                      <SocialCard
                        styles={{ borderBottomRightRadius: 10 }}
                        tweetTitle={
                          'Post Atan Hesapların Toplam Takipçi Sayısı'
                        }
                        tweetCount={touristDisturbanceReport?.totalFollowersCount.toLocaleString()}
                        img={TwitterLogo}
                        color={'#50aaf5'}
                      />
                    </div>
                  </div>

                  <Styled.HashtagsCardContainer>
                    <Styled.HashtagsContainer>
                      <Hashtags data={touristDisturbanceReport?.hashtagDetails} />
                    </Styled.HashtagsContainer>
                    <div style={{ alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.3vw' }}>
                        <SocialCard
                          styles={{ borderBottomLeftRadius: 10 }}
                          tweetTitle={'Haberler'}
                          tweetCount={touristDisturbanceReport?.newsCount.toLocaleString()}
                          icon={'fa-solid fa-newspaper'}
                          color={'#FF9914'}
                        />
                        <SocialCard
                          styles={{ borderBottomRightRadius: 10 }}
                          tweetTitle={'Reddit'}
                          tweetCount={touristDisturbanceReport?.redditCount.toLocaleString()}
                          icon={'fa-brands fa-reddit'}
                          color={'#FF4500'}
                        />
                      </div>
                    </div>
                  </Styled.HashtagsCardContainer>
                </Styled.Container>

                {touristDisturbanceReport?.hashtagDetails
                  ?.filter((item: HashtagDetail) => {
                    // En az bir bölümde veri olan hashtag'leri göster
                    return (
                      (item.tweets && item.tweets.length > 0) ||
                      (item.news && item.news.length > 0) ||
                      (item.reddit && item.reddit.length > 0) ||
                      (item.influentialPeople && item.influentialPeople.length > 0)
                    )
                  })
                  ?.map(
                    (item: HashtagDetail, index: number) => (
                      <Styled.LineComp
                        style={{ borderColor: colors[index], marginTop: '6%' }}
                      >
                        <Styled.TitleWrapper
                          style={{ backgroundColor: colors[index] }}
                        >
                          <Styled.Title>&#9679; {item?.hashtag}</Styled.Title>
                          <Styled.Line
                            style={{ backgroundColor: colors[index] }}
                          />
                        </Styled.TitleWrapper>
                        
                        {/* Tweets - sadece veri varsa göster */}
                        {item?.tweets && item.tweets.length > 0 && (
                          <Styled.Row id="tweet">
                            <Styled.Line
                              style={{ backgroundColor: colors[index] }}
                            />
                            <Overlay
                              label={'X'}
                              topic={item.hashtag}
                              color={'#50aaf5'}
                              img={TwitterLogo}
                              titleColor={colors[index]}
                            >
                              {item?.tweets?.map((tweet: any) => (
                                <TwitterTweetCard tweet={tweet} />
                              ))}
                            </Overlay>
                          </Styled.Row>
                        )}

                        {/* News - sadece veri varsa göster */}
                        {item?.news && item.news.length > 0 && (
                          <Styled.Row id="news">
                            <Styled.Line
                              style={{ backgroundColor: colors[index] }}
                            />
                            <Overlay
                              label={'Haberler'}
                              topic={item.hashtag}
                              color={'#FF9914'}
                              icon={'fa-solid fa-newspaper'}
                              titleColor={colors[index]}
                            >
                              {item?.news.map((news: any, index: number) => (
                                <NewsCard
                                  key={index}
                                  news={news}
                                  index={index}
                                  updateShowFullTextList={updateShowFullTextList}
                                  showFullTextList={showFullTextList}
                                />
                              ))}
                            </Overlay>
                          </Styled.Row>
                        )}

                        {/* Reddit - sadece veri varsa göster */}
                        {item?.reddit && item.reddit.length > 0 && (
                          <Styled.Row id="reddit">
                            <Styled.Line
                              style={{ backgroundColor: colors[index] }}
                            />
                            <Overlay
                              label={'Reddit'}
                              topic={item.hashtag}
                              color={'#FF4500'}
                              icon={'fa-brands fa-reddit'}
                              titleColor={colors[index]}
                            >
                              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'flex-start', width: '100%', margin: 0 }}>
                                {item?.reddit.map((reddit: any, redditIndex: number) => (
                                  <RedditCard
                                    key={redditIndex}
                                    reddit={reddit}
                                    index={redditIndex}
                                    updateShowFullTextList={updateShowFullTextList}
                                    showFullTextList={showFullTextList}
                                  />
                                ))}
                              </div>
                            </Overlay>
                          </Styled.Row>
                        )}

                        {/* Influential People - sadece veri varsa göster */}
                        {item?.influentialPeople && item.influentialPeople.length > 0 && (
                          <Styled.StyledDiv id="user">
                            <div
                              style={{
                                position: 'absolute',
                                left: '0px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                height: '2px',
                                width: '20px',
                                backgroundColor: colors[index],
                              }}
                            ></div>
                            <Overlay
                              label={'Kişiler'}
                              topic={item.hashtag}
                              color={'#3658A5'}
                              titleColor={colors[index]}
                            >
                              {item?.influentialPeople?.map((itm: any) => (
                                <Users people={itm} />
                              ))}
                            </Overlay>
                          </Styled.StyledDiv>
                        )}
                      </Styled.LineComp>
                    )
                  )}

                {/* WordCloud Section */}
                {touristDisturbanceReport?.wordCounts && touristDisturbanceReport.wordCounts.length > 0 && (
                  <div style={{ marginTop: '40px', padding: '20px' }}>
                    <Styled.TitleWrapper style={{ backgroundColor: '#4CAF50', marginBottom: '20px' }}>
                      <Styled.Title style={{ color: 'white' }}>&#9679; Kelime Bulutu</Styled.Title>
                      <Styled.Line style={{ backgroundColor: '#4CAF50' }} />
                    </Styled.TitleWrapper>
                    <WordCloud data={touristDisturbanceReport.wordCounts} />
                  </div>
                )}
              </div>
            </PdfContainer>
          </Row>
        </Page>
      </Container>
    </MainLayout>
  )
}

export default MobileReport 